import { atom, useAtom } from "jotai";
import { supabase } from "../lib/supabase";
import type {
  SearchParams,
  SortParams,
  FetchResult,
} from "../components/Manager/types";
import { useEffect, useCallback } from "react";
import type { Json } from "../types/supabase";

export type Supplier = {
  id: number;
  name: string;
  agency_id: number | null;
  agencies?: { name: string } | null;
  requirement_template_id: number;
  product_template_labels: Json | null;
  created_at: string;
  updated_at: string;
};

/**
 * Search suppliers by name prefix. Returns empty array for empty search string.
 * Used by autocomplete components.
 */
export async function searchSuppliers(
  searchText: string,
  limit = 10,
): Promise<Supplier[]> {
  if (!searchText.trim()) {
    return [];
  }

  const { data, error } = await supabase
    .from("suppliers")
    .select("*")
    .ilike("name", `%${searchText}%`)
    .order("name", { ascending: true })
    .limit(limit);

  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch a single supplier by ID. Used to display the selected supplier name.
 */
export async function fetchSupplierById(id: number): Promise<Supplier | null> {
  const { data, error } = await supabase
    .from("suppliers")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // Not found
    throw error;
  }
  return data;
}

export async function fetchSuppliers(
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
  sortParams?: SortParams,
): Promise<FetchResult<Supplier>> {
  // First, get total count with search filters
  let countQuery = supabase
    .from("suppliers")
    .select("*", { count: "exact", head: true });

  // Apply text search filter for count (only on name)
  if (searchParams?.text) {
    const searchText = `%${searchParams.text}%`;
    countQuery = countQuery.ilike("name", searchText);
  }

  const { count } = await countQuery;

  // Now get the data with agency name
  let query = supabase.from("suppliers").select(`
    *,
    agencies(name)
  `);

  // Apply sorting (default to name ascending if not specified)
  const sortColumn = sortParams?.column ?? "name";
  const sortAscending = sortParams?.order !== "desc";
  query = query.order(sortColumn, { ascending: sortAscending });

  // Apply text search filter (only on name)
  if (searchParams?.text) {
    const searchText = `%${searchParams.text}%`;
    query = query.ilike("name", searchText);
  }

  if (limit) {
    query = query.limit(limit + 1);
  }

  if (offset) {
    query = query.range(offset, offset + (limit ?? 10));
  }

  const { error, data } = await query;

  if (error) throw error;

  const items = data ?? [];
  const hasMore = limit ? items.length > limit : false;
  const limitedData = limit ? items.slice(0, limit) : items;

  return { data: limitedData, hasMore, totalCount: count ?? undefined };
}

const suppliersAtom = atom<Supplier[]>([]);

export function useSuppliers() {
  const [suppliers, setSuppliers] = useAtom(suppliersAtom);

  const fetchAllSuppliers = useCallback(async () => {
    try {
      const { error, data } = await supabase
        .from("suppliers")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setSuppliers(data ?? []);
    } catch (err) {
      console.error("Error fetching suppliers:", err);
    }
  }, [setSuppliers]);

  useEffect(() => {
    fetchAllSuppliers();
  }, [fetchAllSuppliers]);

  return [suppliers, fetchAllSuppliers] as const;
}

// --- Requirement Templates ---

export type RequirementTemplate = {
  id: number;
  name: string;
  require_store: boolean;
};

const requirementTemplatesAtom = atom<RequirementTemplate[]>([]);

export function useRequirementTemplates() {
  const [templates, setTemplates] = useAtom(requirementTemplatesAtom);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("requirement_templates")
        .select("id, name, require_store")
        .order("name", { ascending: true });

      if (error) throw error;
      setTemplates(data ?? []);
    } catch (err) {
      console.error("Error fetching requirement templates:", err);
    }
  }, [setTemplates]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return templates;
}

// --- Product Requirements (for label editor) ---

export type ProductRequirement = {
  id: number;
  description: string;
  code: string;
  order: number;
  labels: Json;
};

/**
 * Fetch product requirements for a given requirement template.
 * Used to populate the label editor with requirement descriptions.
 */
export async function fetchProductRequirementsByTemplate(
  templateId: number,
): Promise<ProductRequirement[]> {
  const { data, error } = await supabase
    .from("product_requirements")
    .select("id, description, code, order, labels")
    .eq("requirement_template_id", templateId)
    .order("order", { ascending: true });

  if (error) throw error;
  return data ?? [];
}

// --- Update Supplier ---

export async function updateSupplier(
  id: number,
  fields: {
    requirement_template_id?: number;
    product_template_labels?: Json | null;
    agency_id?: number | null;
  },
): Promise<void> {
  const updatePayload: Record<string, unknown> = {};
  if (fields.requirement_template_id !== undefined) {
    updatePayload.requirement_template_id = fields.requirement_template_id;
  }
  if (fields.product_template_labels !== undefined) {
    updatePayload.product_template_labels = fields.product_template_labels;
  }
  if (fields.agency_id !== undefined) {
    updatePayload.agency_id = fields.agency_id;
  }

  const { error } = await supabase
    .from("suppliers")
    .update(updatePayload)
    .eq("id", id);

  if (error) {
    throw new Error(error.message);
  }
}
